// useCicdRun - startet Snapshot + Dispatch + pollt die GitHub-Actions-Laeufe.
// Nutzt bestehende Endpoints (Transport-Logik ist eingefroren):
//   POST /api/cicd/transport/snapshot   (inkl. partnerIds -> PD-Dateien im Snapshot)
//   POST /api/cicd/transport/dispatch  -> {laeufe:[{packageId,run:{id,status,html_url}|null,error?,hinweis?}]}
//                                          ODER {status:"pending_approval"}
//   GET  /api/cicd/pipeline/overview?spaceId=... (Polling ~6s)
// ⛔ KEIN direkter PD-Write mehr: das Partner Directory geht ausschliesslich ueber die Pipeline
//    (partnerIds -> Snapshot -> Workflow sync-partner-directory.yml am Ziel). Der fruehere
//    Direktaufruf /api/transport/replicate-partner-directory ist abgeschaltet.

import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../../utils/helpers";
import { t as tr } from "../i18n";

export function useCicdRun() {
  // idle|snapshotting|dispatching|running|pending|dispatched|done|failed
  // "dispatched" = abgeschickt, aber kein Lauf auffindbar (siehe unten).
  const [state, setState] = useState("idle");
  const [runId, setRunId] = useState(null);
  const [runUrl, setRunUrl] = useState(null);
  const [runInfo, setRunInfo] = useState(null);
  const [error, setError] = useState(null);
  // Nebenmeldungen, die den Transport nicht abbrechen: nicht ladbare Artefakte,
  // uebersprungene Partner-Kennungen, Pakete ohne eigenen Lauf. Sie muessen sichtbar
  // bleiben - sonst haelt der Anwender einen Teiltransport fuer vollstaendig.
  const [meldungen, setMeldungen] = useState([]);
  const timerRef = useRef(null);

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  // Beobachtet ALLE gestarteten Laeufe in EINEM Tick. Bei mehreren Werken laeuft je
  // Zielpaket ein eigener Workflow; frueher wurde nur der erste gepollt und der
  // Assistent meldete Erfolg, waehrend Lauf 2 rot lief.
  const startPolling = useCallback((spaceId, beobachtet) => {
    if (timerRef.current) clearInterval(timerRef.current);
    const liste = (Array.isArray(beobachtet) ? beobachtet : []).filter((l) => l && l.id != null);
    if (!liste.length) return;
    const tick = async () => {
      try {
        const r = await api(`/api/cicd/pipeline/overview?spaceId=${encodeURIComponent(spaceId)}`);
        const runs = Array.isArray(r?.runs) ? r.runs : [];
        const stand = liste.map((l) => ({ ...l, run: runs.find((x) => String(x.id) === String(l.id)) || null }));
        // Die Live-Karte zeigt weiterhin EINEN Lauf - den zuerst gestarteten. Der
        // Gesamtzustand darunter fasst dagegen alle beobachteten Laeufe zusammen.
        const ersterTreffer = stand.find((x) => x.run);
        if (ersterTreffer) setRunInfo(ersterTreffer.run);
        const gescheitert = stand.filter((x) => x.run && x.run.status === "completed" && x.run.conclusion !== "success");
        if (gescheitert.length) {
          if (timerRef.current) clearInterval(timerRef.current);
          const namen = gescheitert.map((x) => x.packageId || x.id).join(", ");
          const text = gescheitert.length === 1
            ? tr("run.pkgFailed", { pkg: namen })
            : tr("run.pkgsFailed", { n: gescheitert.length, liste: namen });
          setError((prev) => (prev ? `${prev} ${text}` : text));
          setState("failed");
          return;
        }
        const alleFertig = stand.every((x) => x.run && x.run.status === "completed" && x.run.conclusion === "success");
        if (alleFertig) {
          if (timerRef.current) clearInterval(timerRef.current);
          setState("done");
        }
      } catch {
        /* stiller Fehler, weiterpollen */
      }
    };
    tick();
    // 6s statt 3s: der CI/CD-Monitor pollt aktive Laeufe mit 5s, schneller muss der
    // Assistent nicht sein - und das GitHub-Kontingent haelt laenger.
    timerRef.current = setInterval(tick, 6000);
  }, []);

  const start = useCallback(async ({ spaceId, sourceInstanceId, packageId, packageName, artifactIds, globalRefs, werke, configsByWerk, partnerIds, snapshotPartnerIds, pdStagesByPid, commitMessage, targetStage, deployment }) => {
    // Etappe C: Der Snapshot fuehrt ALLE ausgewaehlten Pids samt Stufen-Filter
    // (.stages.json je Pid), der Dispatch nur die Pids fuer die aktuelle Zielstufe.
    // Wird snapshotPartnerIds nicht mitgegeben, faellt der Snapshot auf partnerIds
    // zurueck - so bleibt jeder Aufrufer der Alt-Signatur weiter kompatibel.
    const pidsFuerSnapshot = Array.isArray(snapshotPartnerIds) ? snapshotPartnerIds : (Array.isArray(partnerIds) ? partnerIds : []);
    const pidsFuerDispatch = Array.isArray(partnerIds) ? partnerIds : [];
    setState("snapshotting");
    setError(null);
    setMeldungen([]);
    setRunId(null);
    setRunUrl(null);
    setRunInfo(null);
    let snapshotRes;
    try {
      snapshotRes = await api("/api/cicd/transport/snapshot", {
        method: "POST",
        timeout: 180000,
        body: JSON.stringify({
          spaceId,
          sourceInstanceId,
          packageId,
          packageName,
          artifactIds,
          deployment,
          targetStage,
          // Selected global references (script collections). The snapshot stores them under
          // ScriptCollections/<Id>/ and the target workflow deploys them BEFORE the iFlows.
          globalRefs: Array.isArray(globalRefs) ? globalRefs : [],
          // Selected plants: the snapshot materializes one package folder per kennung, with
          // suffixed artifact ids. Empty means no split. Jedes Werk traegt seine EIGENE
          // Artefakt-Auswahl (artifactIds) - ohne sie vervielfacht das Backend die flache
          // Liste auf alle Werke und eine Abwahl in Schritt 3 bliebe wirkungslos.
          werke: Array.isArray(werke) ? werke : [],
          // Parameter values per plant and stage. The backend writes SAP's
          // Configuration/ConfigParams_INTEGRATION_FLOW.json per target package from them and
          // merges with the Git stock, so values of other stages survive.
          configsByWerk: configsByWerk && typeof configsByWerk === "object" ? configsByWerk : {},
          // Ausgewaehlte Partner-Kennungen mit in den Snapshot: das Backend
          // exportiert sie unter IntegrationPackages/<pkg>/PartnerDirectory/<Pid>/...
          // Ohne partnerIds bleibt das Snapshot unveraendert - PD-Dateien werden
          // nur erzeugt, wenn hier eine nicht-leere Liste ankommt.
          partnerIds: pidsFuerSnapshot,
          // Etappe C: je Pid der Stufen-Satz. Fehlt der Eintrag fuer eine Pid,
          // schreibt das Backend "*" in .stages.json (Altverhalten, alle Stufen).
          pdStagesByPid: pdStagesByPid && typeof pdStagesByPid === "object" ? pdStagesByPid : {},
          commitMessage: commitMessage || `Transport ${packageName || packageId}`,
        }),
      });
    } catch (e) {
      setError(e?.message || "Snapshot fehlgeschlagen");
      setState("failed");
      throw e;
    }

    // Nebenbefunde aus dem Snapshot sichtbar machen. Das Backend bricht bei nicht ladbaren
    // AUSGEWAEHLTEN Artefakten hart ab (502); kommen die Listen trotzdem in einer OK-Antwort
    // mit, betreffen sie Randfaelle - lautlos verschwinden duerfen sie deshalb nicht.
    const nichtGeladen = (Array.isArray(snapshotRes?.failed) ? snapshotRes.failed : []).filter(Boolean);
    const pdNichtGeladen = (Array.isArray(snapshotRes?.pdFailed) ? snapshotRes.pdFailed : []).filter(Boolean);
    const nebenmeldungen = [
      ...nichtGeladen.map((f) => ({
        art: "fehler",
        text: tr("run.artFailedItem", {
          id: f.id || f.artifactId || "?",
          grund: f.reason || f.grund || tr("run.grundUnbekannt"),
        }),
      })),
      ...pdNichtGeladen.map((p) => ({
        art: "hinweis",
        text: tr("run.pdFailedItem", {
          pid: p.pid || p.Pid || "?",
          grund: p.grund || p.reason || tr("run.grundUnbekannt"),
        }),
      })),
    ];
    if (nebenmeldungen.length) setMeldungen(nebenmeldungen);
    if (nichtGeladen.length) setError(tr("run.artFailed", { n: nichtGeladen.length }));
    if (pdNichtGeladen.length && !nichtGeladen.length) setError(tr("run.pdFailed", { n: pdNichtGeladen.length }));

    // The target packages come from the snapshot response, they are NOT rebuilt here. The
    // snapshot just created them and knows their ids; a second naming rule maintained in parallel
    // in the frontend has been a repeat source of bugs.
    const zielPakete = (Array.isArray(snapshotRes?.pakete) ? snapshotRes.pakete : [])
      .map((p) => ({ packageId: p.packageId, packageName: p.packageName, artifactIds: p.artefakte || [] }))
      .filter((p) => p.packageId);
    if (!zielPakete.length) {
      const msg = "Der Snapshot hat kein Zielpaket gemeldet - der Transport wird nicht gestartet.";
      setError(msg);
      setState("failed");
      throw new Error(msg);
    }

    setState("dispatching");
    let dispatchRes;
    try {
      dispatchRes = await api("/api/cicd/transport/dispatch", {
        method: "POST",
        // One run per target package, started one after the other. With several plants this takes
        // correspondingly longer than the single call it used to be.
        timeout: 120000,
        body: JSON.stringify({
          spaceId,
          // Kein stiller "qas"-Fallback mehr: das Backend prueft die Zielstufe und meldet
          // fachlich, wenn sie fehlt oder nicht vorwaerts liegt. Raten ist hier gefaehrlicher
          // als ein klarer Fehler.
          targetStage: String(targetStage || "").toLowerCase(),
          sourceSha: snapshotRes?.commit?.sha || snapshotRes?.commit?.commit?.sha,
          expectedTransportModus: deployment === undefined ? undefined : "erweitert",
          language: localStorage.getItem("iio-lang") === "en" ? "en" : "de",
          pakete: zielPakete,
          // Selektierte Partner-Directory-Pids: loesen im Ziel-Workflow den PD-Merge-Upsert aus.
          // Etappe C: hier NUR die fuer die aktuelle Zielstufe freigegebenen Pids - Pids, die
          // erst spaeter (z. B. PROD) greifen sollen, liegen im Snapshot, gehen aber nicht in
          // diesen Dispatch.
          partnerIds: pidsFuerDispatch.join(","),
          commitMessage: commitMessage || `Transport ${packageName || packageId}`,
        }),
      });
    } catch (e) {
      setError(e?.message || "Workflow-Start fehlgeschlagen");
      setState("failed");
      throw e;
    }

    if (dispatchRes?.status === "pending_approval") {
      setState("pending");
      return { pendingApproval: true, failed: nichtGeladen, pdFailed: pdNichtGeladen };
    }

    const laeufe = Array.isArray(dispatchRes?.laeufe) ? dispatchRes.laeufe : [];
    // Do not swallow partial failures: what runs, runs - but the user has to see which package
    // did not start, otherwise they take the transport for complete.
    if (dispatchRes?.teilfehler) setError((prev) => (prev ? `${prev} ${dispatchRes.teilfehler}` : dispatchRes.teilfehler));

    // Pakete ohne eigenen Lauf: Grund bzw. Hinweis steht am Eintrag und muss sofort sichtbar
    // sein - im Polling taucht so ein Paket nie wieder auf.
    const ohneLauf = laeufe.filter((l) => !l?.run?.id);
    if (ohneLauf.length) {
      setMeldungen((prev) => [
        ...prev,
        ...ohneLauf.map((l) => ({
          art: l?.error ? "fehler" : "hinweis",
          text: tr("run.pkgLine", { pkg: l?.packageId || "?", text: l?.error || l?.hinweis || tr("run.keinLauf") }),
        })),
      ]);
    }

    // Das Backend sendet html_url (nicht url) - url bleibt nur als Fallback fuer aeltere Antworten.
    const beobachtet = laeufe
      .filter((l) => l?.run?.id)
      .map((l) => ({ id: l.run.id, packageId: l.packageId || null, url: l.run.html_url || l.run.url || null }));
    if (!beobachtet.length && dispatchRes?.run?.id) {
      beobachtet.push({
        id: dispatchRes.run.id,
        packageId: null,
        url: dispatchRes.run.html_url || dispatchRes.run.url || null,
      });
    }

    if (beobachtet.length) {
      setRunId(beobachtet[0].id);
      setRunUrl(beobachtet[0].url);
      setState("running");
      startPolling(spaceId, beobachtet);
      return {
        pendingApproval: false,
        runId: beobachtet[0].id,
        runUrl: beobachtet[0].url,
        laeufe,
        pakete: zielPakete,
        failed: nichtGeladen,
        pdFailed: pdNichtGeladen,
      };
    }

    // Kein einziger Lauf auffindbar: der Dispatch ging raus, aber GitHub meldet keine Lauf-Id
    // zurueck. Frueher blieb die Karte dafuer ewig auf "laeuft" stehen und der Startknopf war
    // tot. Eigener Zustand: der Transport ist angestossen, sein Stand steht im CI/CD-Monitor.
    setState("dispatched");
    const keinLaufText = tr("run.noRunFound");
    setError((prev) => (prev ? `${prev} ${keinLaufText}` : keinLaufText));
    return {
      pendingApproval: false,
      keinLauf: true,
      laeufe,
      pakete: zielPakete,
      failed: nichtGeladen,
      pdFailed: pdNichtGeladen,
    };
  }, [startPolling]);

  const stop = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  return { state, runId, runUrl, runInfo, error, meldungen, start, stop };
}
